import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
    Container,
    Header,
    Table,
    Box,
    StatusIndicator,
    Alert,
    TextFilter,
    Pagination,
    SpaceBetween
} from '@amzn/awsui-components-console';
import JsonDataViewer from './JsonDataViewer';
import './ExcelPreview.scss';

interface ExcelPreviewProps {
    file: File;
    onClose?: () => void;
}

interface TableData {
    headers: string[];
    rows: any[][];
}

const ExcelPreview: React.FC<ExcelPreviewProps> = ({ file, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tableData, setTableData] = useState<TableData | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterText, setFilterText] = useState('');
    const [htmlContent, setHtmlContent] = useState<string>('');
    
    const itemsPerPage = 50;

    useEffect(() => {
        const loadFile = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check if it's a JSON file
                if (file.type === 'application/json' || file.name.endsWith('.json')) {
                    // Handle JSON preview
                    const text = await file.text();
                    const jsonContent = JSON.parse(text);
                    
                    // Store JSON content for rendering
                    (window as any).__JSON_PREVIEW_CONTENT__ = jsonContent;
                    setHtmlContent('json-preview');
                    setLoading(false);
                    return;
                }

                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                
                // Get the first sheet
                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) {
                    throw new Error('No sheets found in the file');
                }
                
                const worksheet = workbook.Sheets[firstSheetName];
                
                // Convert to HTML for display
                const html = XLSX.utils.sheet_to_html(worksheet);
                setHtmlContent(html);
                
                // Also convert to JSON for table display
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    raw: false
                }) as any[][];
                
                if (jsonData.length > 0) {
                    // First row as headers
                    const headers = jsonData[0].map((h, i) => h?.toString() || `Column ${i + 1}`);
                    const rows = jsonData.slice(1);
                    
                    setTableData({
                        headers,
                        rows
                    });
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error loading Excel file:', err);
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        };

        if (file) {
            loadFile();
        }
    }, [file]);

    if (loading) {
        return (
            <Container
                header={
                    <Header variant="h2">
                        File Preview: {file.name}
                    </Header>
                }
            >
                <Box textAlign="center" padding="xl">
                    <StatusIndicator type="loading">
                        Loading file preview...
                    </StatusIndicator>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container
                header={
                    <Header variant="h2">
                        File Preview: {file.name}
                    </Header>
                }
            >
                <Alert type="error" header="Failed to load file">
                    {error}
                </Alert>
            </Container>
        );
    }

    // Filter rows based on search text
    const filteredRows = tableData?.rows.filter(row =>
        row.some(cell => 
            cell?.toString().toLowerCase().includes(filterText.toLowerCase())
        )
    ) || [];

    // Paginate filtered rows
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filteredRows.slice(startIndex, endIndex);

    // Convert to table items format
    const tableItems = paginatedRows.map((row, index) => {
        const item: any = { _index: startIndex + index + 1 };
        tableData?.headers.forEach((header, i) => {
            item[header] = row[i] || '';
        });
        return item;
    });

    // Define column definitions
    const columnDefinitions = [
        {
            id: '_index',
            header: '#',
            cell: (item: any) => item._index,
            width: 50
        },
        ...(tableData?.headers.map(header => ({
            id: header,
            header: header,
            cell: (item: any) => item[header] || '',
            sortingField: header
        })) || [])
    ];

    // Handle JSON preview separately
    if (htmlContent === 'json-preview') {
        const jsonContent = (window as any).__JSON_PREVIEW_CONTENT__ || (window as any).__TEMPLATE_PREVIEW_CONTENT__;
        const mermaidDiagram = (window as any).__TEMPLATE_PREVIEW_MERMAID__;
        const aiSummary = (window as any).__TEMPLATE_PREVIEW_AI_SUMMARY__;
        
        return (
            <Container
                header={
                    <Header 
                        variant="h2"
                        description={`${file.size ? `Size: ${(file.size / 1024).toFixed(2)} KB` : ''} | Type: JSON`}
                    >
                        Template Preview: {file.name}
                    </Header>
                }
            >
                <div 
                    style={{ 
                        backgroundColor: '#ffffff',
                        padding: '16px',
                        borderRadius: '4px',
                        maxHeight: '600px',
                        overflow: 'auto'
                    }}
                >
                    {jsonContent ? (
                        <JsonDataViewer
                            data={jsonContent}
                            mermaidDiagram={mermaidDiagram}
                            aiSummary={aiSummary}
                            defaultView="grid"
                            style={{
                                maxHeight: '600px'
                            }}
                            collapsed={false}
                            displayObjectSize={true}
                            enableClipboard={true}
                        />
                    ) : (
                        <Box textAlign="center" padding="xl">
                            <StatusIndicator type="loading">Loading preview...</StatusIndicator>
                        </Box>
                    )}
                </div>
            </Container>
        );
    }
    
    return (
        <Container
            header={
                <Header 
                    variant="h2"
                    description={`${file.size ? `Size: ${(file.size / 1024).toFixed(2)} KB` : ''} | Type: ${file.type || 'CSV/Excel'}`}
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Box>
                                <StatusIndicator type="success">
                                    {filteredRows.length} rows
                                </StatusIndicator>
                            </Box>
                        </SpaceBetween>
                    }
                >
                    File Preview: {file.name}
                </Header>
            }
        >
            <SpaceBetween size="m">
                <TextFilter
                    filteringText={filterText}
                    onChange={({ detail }) => {
                        setFilterText(detail.filteringText);
                        setCurrentPage(1);
                    }}
                    filteringPlaceholder="Search in data..."
                    filteringAriaLabel="Filter table data"
                />
                
                {/* Table view for structured display */}
                <div className="excel-preview-container">
                    <Table
                        items={tableItems}
                        columnDefinitions={columnDefinitions}
                        variant="embedded"
                        stripedRows
                        empty={
                            <Box textAlign="center" padding="m">
                                No data to display
                            </Box>
                        }
                        footer={
                            <Pagination
                                currentPageIndex={currentPage}
                                onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                                pagesCount={Math.ceil(filteredRows.length / itemsPerPage)}
                                ariaLabels={{
                                    nextPageLabel: 'Next page',
                                    previousPageLabel: 'Previous page',
                                    pageLabel: (pageNumber) => `Page ${pageNumber}`
                                }}
                            />
                        }
                    />
                </div>
            </SpaceBetween>
        </Container>
    );
};

export default ExcelPreview;
